import {ApiError, Checkout} from "@connox/typescript-library";
import {BraintreeError} from "braintree-web";
import {PossibleNetworkError} from "../../../cart/ShoppingCart/ShoppingCart";
import {isSameAddress} from "../AddressStep/AddressStep";
import {authReplacer, captureSentryException} from "../../../../helpers/sentryHelper";
import i18n from "i18next";
import {DEFAULT_ERROR_MESSAGE} from "../../Checkout";
import {BraintreeErrorTypes} from "braintree-web/core";

interface NestedError extends Error {
  error: Error;
}

export interface IBraintreeError extends Error {
  code: string;
  message: string;
  type: BraintreeErrorTypes;
  details: {
    originalError: IBraintreeError | NestedError;
  };
}

export const summaryErrors = (
  err: ApiError | BraintreeError | Error,
  checkout: Checkout,
  occurence: string,
  additional?: Record<string, unknown>,
) => {
  // Get that original error out of the tslib ApiError
  const BTOrDefaultError: Error | IBraintreeError = ((err as ApiError).originalError || err) as Error;
  // Get the deepest error, that is _possibly_ nested in the BraintreeError
  const defaultOrNetworkError: PossibleNetworkError | Error = findRootError(BTOrDefaultError);

  // Give the default message on the error
  let errorMessages: string[];
  // Possibly an error returned by our api?
  if (isPossibleNetworkError(defaultOrNetworkError)) {
    // overwrite default message with messages from api
    errorMessages = defaultOrNetworkError.response?.data.messages?.map((msg) => msg.message) || [];
  } else if (defaultOrNetworkError.message) {
    errorMessages = defaultOrNetworkError.message.split("\u000A");
  } else {
    // Dummy message, to result in an unkown message below
    errorMessages = ["no_error_message_found"];
  }

  if (
    errorMessages.includes("Billing line1 format is invalid.") &&
    errorMessages.includes("Shipping line1 format is invalid.") &&
    isSameAddress(checkout.invoiceAddress, checkout.deliveryAddress)
  ) {
    errorMessages.splice(errorMessages.indexOf("Billing line1 format is invalid."), 1);
    errorMessages.splice(errorMessages.indexOf("Shipping line1 format is invalid."), 1);
    errorMessages.push("Shipping/Billing line1 format is invalid.");
  }

  const {knownMessages, unknownMessages} = errorMessages.reduce<{knownMessages: string[]; unknownMessages: string[]}>(
    (acc, curr) => {
      if (i18n.exists(curr, {ns: "errorcodes"})) {
        acc.knownMessages.push(curr);
      } else if (curr === "Set Up Error - Card") {
        acc.knownMessages.push("Invalid Transaction");
      } else {
        acc.unknownMessages.push(authReplacer(curr));
      }

      return acc;
    },
    {knownMessages: [], unknownMessages: []},
  );

  // if any error is unknown show a default error msg and capture a sentry exception
  let sentryEventId: string;
  if (unknownMessages.length) {
    BTOrDefaultError.name = (err as Error).name;
    sentryEventId = captureSentryException(BTOrDefaultError, checkout, occurence || "Summary Purchase Button", {
      ...additional,
      knownMessages,
      unknownMessages,
    });

    knownMessages.push(DEFAULT_ERROR_MESSAGE);
  }

  return knownMessages.map((msg) => i18n.t(msg, {ns: ["translations", "errorcodes"], sentryEventId}));
};

function isPossibleNetworkError(error: Error | PossibleNetworkError): error is PossibleNetworkError {
  return !!(error as PossibleNetworkError).response?.data?.messages;
}

// node_modules/braintree-web/lib/braintree-error.js
export function findRootError(err: Error | IBraintreeError | NestedError): Error {
  if (
    err.name &&
    err.name.endsWith("BraintreeError") &&
    (err as IBraintreeError).details &&
    (err as IBraintreeError).details.originalError
  ) {
    return findRootError((err as IBraintreeError).details.originalError);
  }

  // Why that mess? -> https://github.com/Connox/templates-connox/pull/1715/files -> line: 103 and following
  if ((err as NestedError).error) {
    return (err as NestedError).error;
  }

  return err;
}
